// ============================================================================
// PairsForm — Échelle notée d'un questionnaire (rendu UNIQUE)
// ============================================================================
// @systeme: questionnaires
//
// UN SEUL rendu de l'échelle, partagé par l'ÉDITEUR (aperçu) et le RÉPONDANT.
//
// Pourquoi ce composant existe (FX, recette 2026-08-21) : l'éditeur prévisualise
// une échelle en étoiles — le menu la nomme même « Échelle (étoiles) » — pendant
// que le participant recevait une pile de boutons radio numérotés. Le concepteur
// du questionnaire voyait donc autre chose que son répondant : l'aperçu mentait.
// Deux rendus pour une même question, c'est la garantie qu'ils divergeront
// encore. Il n'y en a plus qu'un.
//
// Choix du rendu — l'échelle a deux visages légitimes :
//   • ÉTOILES quand la question est une appréciation nue (« pas bien » → « très
//     bien »). C'est lisible d'un coup d'œil et ça se remplit d'un clic.
//   • BOUTONS quand l'auteur a écrit des LIBELLÉS par valeur : le texte porte
//     alors le sens (« 1 - jamais », « 5 - systématiquement »), et une étoile ne
//     sait pas le dire. Le fond prime sur la forme.
//   • BOUTONS aussi quand l'échelle ne commence pas à 1 ou dépasse 10 : les
//     étoiles ne savent représenter ni un 0 ni une échelle longue.
// ============================================================================

import { Box, Rating, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";

export interface EchelleOptions {
  min?: number;
  max?: number;
  /** Libellés par valeur, saisis par l'auteur : « {"1": "jamais", ...} ». */
  labels?: Record<string, string>;
}

interface Props {
  options?: EchelleOptions | null;
  /** Valeur courante (string ou number selon la source). `null` = non répondu. */
  value?: string | number | null;
  onChange?: (valeur: string) => void;
  /** Aperçu de l'éditeur : rendu identique, mais non modifiable. */
  readOnly?: boolean;
}

/** Vrai si l'échelle se prête aux étoiles (cf. en-tête). */
function echelleEnEtoiles(options?: EchelleOptions | null): boolean {
  const min = options?.min ?? 1;
  const max = options?.max ?? 5;
  const aDesLibelles = !!options?.labels && Object.keys(options.labels).length > 0;
  return !aDesLibelles && min === 1 && max >= 2 && max <= 10;
}

export default function EchelleReponse({ options, value, onChange, readOnly = false }: Props) {
  const min = options?.min ?? 1;
  const max = options?.max ?? 5;
  const valeurNum =
    value === null || value === undefined || value === "" ? 0 : Number(value);
  const valeurStr = value === null || value === undefined ? "" : String(value);

  // ---- Rendu ÉTOILES ------------------------------------------------------
  if (echelleEnEtoiles(options)) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1, flexWrap: "wrap" }}>
        <Rating
          value={Number.isFinite(valeurNum) ? valeurNum : 0}
          max={max}
          readOnly={readOnly}
          onChange={(_, v) => onChange?.(String(v ?? ""))}
          sx={{
            "& .MuiRating-iconFilled": { color: "#4BB7A5" },
            "& .MuiRating-iconHover": { color: "#3a9a87" },
            fontSize: "2.2rem",
          }}
        />
        <Typography color="text.secondary" variant="body2">
          {valeurNum > 0 ? `${valeurNum} / ${max}` : `Cliquez pour noter de 1 à ${max}`}
        </Typography>
      </Box>
    );
  }

  // ---- Rendu BOUTONS ------------------------------------------------------
  // Une rangée compacte plutôt qu'une pile de radios : même information, quatre
  // fois moins de hauteur, et le sens de l'échelle reste annoncé.
  const valeurs = Array.from({ length: max - min + 1 }, (_, i) => i + min);
  const aDesLibelles = !!options?.labels && Object.keys(options.labels).length > 0;

  return (
    <Box sx={{ mt: 1 }}>
      {!aDesLibelles && (
        // Sens de l'échelle toujours annoncé (retour FX 2026-08-19 : « des
        // plaintes pour manque de clarté »). Sans libellés, le répondant ne
        // voyait que des chiffres nus et devait deviner quel bout était le bon.
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          De <strong>{min}</strong> (le plus bas) à <strong>{max}</strong> (le plus haut)
        </Typography>
      )}
      <ToggleButtonGroup
        exclusive
        value={valeurStr}
        onChange={(_, v) => { if (v !== null && !readOnly) onChange?.(String(v)); }}
        orientation={aDesLibelles ? "vertical" : "horizontal"}
        sx={{
          flexWrap: "wrap",
          gap: 1,
          ...(aDesLibelles ? { alignItems: "stretch", width: "100%" } : {}),
          "& .MuiToggleButton-root": {
            border: "1px solid #e2e8f0",
            borderRadius: "8px !important",
            textTransform: "none",
            px: aDesLibelles ? 2 : 2.5,
            justifyContent: aDesLibelles ? "flex-start" : "center",
            "&.Mui-selected": {
              bgcolor: "#f0fdf9",
              borderColor: "#4BB7A5",
              borderWidth: 2,
              color: "#0f766e",
              fontWeight: 600,
              "&:hover": { bgcolor: "#e3faf4" },
            },
          },
        }}
      >
        {valeurs.map((v) => (
          <ToggleButton key={v} value={String(v)} disabled={readOnly}>
            <strong>{v}</strong>
            {options?.labels?.[String(v)] && (
              <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                {options.labels[String(v)]}
              </Typography>
            )}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}
